import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TiptapImage from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Bold, Italic, List, ListOrdered, Quote, Link as LinkIcon, Strikethrough, Code, Undo, Redo, Image as ImageIcon, Youtube as YoutubeIcon, Upload } from "lucide-react";

/**
 * RichTextEditor — Tiptap-backed WYSIWYG.
 * Stores HTML as a string. Use <RichTextDisplay value=... /> to render.
 *
 * Props:
 *   value (string)      Current HTML
 *   onChange (string)   Fires on every edit with the new HTML
 *   placeholder         Visible when empty
 *   minHeight           min height (default 120px)
 *   compact             smaller toolbar (for comment composers)
 *   workspaceId         Enables paste / drop / upload of inline images
 *                       (hits POST /workspaces/{ws}/rich-text/images).
 *                       When unset, image buttons fall back to URL prompt.
 *   testid              For data-testid on root
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Write here…", minHeight = 120, compact = false, workspaceId = null, testid = "rich-text-editor" }) {
  const fileInputRef = useRef(null);
  // Feb 2026 · Inline image upload. Called from paste, drop, toolbar
  // upload button. Returns the public URL to drop into the editor, or
  // null when workspace or upload fails. Toasts loudly on error.
  const uploadImage = async (fileBlob) => {
    if (!workspaceId) {
      toast.error("Image paste needs a workspace context");
      return null;
    }
    const name = fileBlob.name || `pasted-${Date.now()}.png`;
    const fd = new FormData();
    fd.append("file", fileBlob, name);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/rich-text/images`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // Build an absolute URL so pasted/exported HTML (PDF, email,
      // outside-domain renders) still resolves. `api` already carries
      // REACT_APP_BACKEND_URL via its baseURL; use that root.
      const base = (api.defaults.baseURL || "").replace(/\/api\/v1\/?$/, "");
      return `${base}${data.url}`;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Image upload failed"));
      return null;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-primary underline" } }),
      Placeholder.configure({ placeholder }),
      TiptapImage.configure({ HTMLAttributes: { class: "max-w-full h-auto my-3" } }),
      Youtube.configure({ width: 640, height: 360, allowFullscreen: true, HTMLAttributes: { class: "my-3" } }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Tiptap returns "<p></p>" for empty — normalize to ""
      onChange?.(ed.isEmpty ? "" : html);
    },
    editorProps: {
      // Feb 2026 · Paste & drop handlers for inline images. Grabs any
      // image/* file from the clipboard or drop event, uploads it, and
      // inserts an <img> at the current selection. We don't preventDefault
      // when no image is found so plain-text paste keeps working.
      handlePaste(view, event) {
        const items = event.clipboardData?.items || [];
        const images = Array.from(items).filter((it) => it.kind === "file" && it.type.startsWith("image/"));
        if (images.length === 0) return false;
        event.preventDefault();
        (async () => {
          for (const it of images) {
            const file = it.getAsFile();
            if (!file) continue;
            const url = await uploadImage(file);
            if (url) view.dispatch(view.state.tr.replaceSelectionWith(
              view.state.schema.nodes.image.create({ src: url }),
            ));
          }
        })();
        return true;
      },
      handleDrop(view, event) {
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
        if (files.length === 0) return false;
        event.preventDefault();
        (async () => {
          for (const file of files) {
            const url = await uploadImage(file);
            if (url) view.dispatch(view.state.tr.replaceSelectionWith(
              view.state.schema.nodes.image.create({ src: url }),
            ));
          }
        })();
        return true;
      },
    },
  });

  // Sync external value changes (rare, but safe)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL", editor.getAttributes("link").href || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Image URL (https://…)");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const addYoutube = () => {
    const url = window.prompt("YouTube URL");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url }).run();
  };

  const onUploadClick = () => fileInputRef.current?.click();
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    const url = await uploadImage(file);
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const Btn = ({ on, action, icon: Icon, label, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={action}
      title={label}
      className={`px-2 py-1 text-xs border-r border-border last:border-r-0 hover:bg-muted ${on ? "bg-muted font-bold" : ""}`}
      data-testid={tid}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <div className="border border-border" data-testid={testid}>
      <div className="flex flex-wrap border-b border-border bg-muted/30 text-foreground">
        <Btn on={editor.isActive("bold")} action={() => editor.chain().focus().toggleBold().run()} icon={Bold} label="Bold" tid={`${testid}-bold`} />
        <Btn on={editor.isActive("italic")} action={() => editor.chain().focus().toggleItalic().run()} icon={Italic} label="Italic" tid={`${testid}-italic`} />
        {!compact && (
          <Btn on={editor.isActive("strike")} action={() => editor.chain().focus().toggleStrike().run()} icon={Strikethrough} label="Strikethrough" tid={`${testid}-strike`} />
        )}
        <Btn on={editor.isActive("bulletList")} action={() => editor.chain().focus().toggleBulletList().run()} icon={List} label="Bulleted list" tid={`${testid}-bullet`} />
        <Btn on={editor.isActive("orderedList")} action={() => editor.chain().focus().toggleOrderedList().run()} icon={ListOrdered} label="Numbered list" tid={`${testid}-ordered`} />
        {!compact && (
          <>
            <Btn on={editor.isActive("blockquote")} action={() => editor.chain().focus().toggleBlockquote().run()} icon={Quote} label="Quote" tid={`${testid}-quote`} />
            <Btn on={editor.isActive("code")} action={() => editor.chain().focus().toggleCode().run()} icon={Code} label="Inline code" tid={`${testid}-code`} />
          </>
        )}
        <Btn on={editor.isActive("link")} action={addLink} icon={LinkIcon} label="Link" tid={`${testid}-link`} />
        {!compact && (
          <>
            {workspaceId && (
              <Btn on={false} action={onUploadClick} icon={Upload} label="Upload image (or paste/drop here)" tid={`${testid}-upload-image`} />
            )}
            <Btn on={false} action={addImage} icon={ImageIcon} label="Insert image (URL)" tid={`${testid}-image`} />
            <Btn on={false} action={addYoutube} icon={YoutubeIcon} label="Embed YouTube" tid={`${testid}-youtube`} />
          </>
        )}
        <div className="ml-auto flex">
          <Btn on={false} action={() => editor.chain().focus().undo().run()} icon={Undo} label="Undo" tid={`${testid}-undo`} />
          <Btn on={false} action={() => editor.chain().focus().redo().run()} icon={Redo} label="Redo" tid={`${testid}-redo`} />
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFilePicked}
        data-testid={`${testid}-file-input`}
      />
      <EditorContent
        editor={editor}
        className="px-3 py-2 prose prose-sm max-w-none dark:prose-invert text-sm"
        style={{ minHeight }}
      />
    </div>
  );
}

/** Lightweight read-only renderer for HTML coming from RichTextEditor. */
export function RichTextDisplay({ html, className = "", testid }) {
  // Defer DOMPurify lazy import to keep bundle leaner where it isn't used.
  const [cleanHtml, setCleanHtml] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!html) { setCleanHtml(""); return; }
    import("dompurify").then((mod) => {
      if (cancelled) return;
      const DOMPurify = mod.default || mod;
      // Restrict iframes to YouTube/Vimeo/YT-nocookie only to prevent XSS via arbitrary frame src.
      DOMPurify.addHook("uponSanitizeElement", (node, data) => {
        if (data.tagName === "iframe") {
          const src = (node.getAttribute && node.getAttribute("src")) || "";
          const ok = /^https:\/\/(www\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com|player\.vimeo\.com)\//i.test(src);
          if (!ok) node.parentNode?.removeChild(node);
        }
      });
      setCleanHtml(DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src", "width", "height"],
      }));
      DOMPurify.removeAllHooks();
    });
    return () => { cancelled = true; };
  }, [html]);
  if (!html) return null;
  return (
    <div
      data-testid={testid}
      className={`prose prose-sm max-w-none dark:prose-invert text-sm ${className}`}
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
    />
  );
}
